import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type ResidentCreateDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  submitLabel?: string;
  displayName: string;
  onDisplayNameChange: (value: string) => void;
  email: string;
  onEmailChange: (value: string) => void;
  phone: string;
  onPhoneChange: (value: string) => void;
  addressLine1: string;
  onAddressLine1Change: (value: string) => void;
  city: string;
  onCityChange: (value: string) => void;
  state: string;
  onStateChange: (value: string) => void;
  postalCode: string;
  onPostalCodeChange: (value: string) => void;
  saving: boolean;
  onSubmit: () => void;
};

export function ResidentCreateDialog({
  open,
  onOpenChange,
  title = "Add resident",
  description = "Create a resident profile manually. Concerns and permits can be linked afterward.",
  submitLabel = "Add resident",
  displayName,
  onDisplayNameChange,
  email,
  onEmailChange,
  phone,
  onPhoneChange,
  addressLine1,
  onAddressLine1Change,
  city,
  onCityChange,
  state,
  onStateChange,
  postalCode,
  onPostalCodeChange,
  saving,
  onSubmit,
}: ResidentCreateDialogProps) {
  const canSubmit = displayName.trim().length > 0 || email.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="resident-display-name">Display name</Label>
            <Input
              id="resident-display-name"
              value={displayName}
              onChange={(event) => onDisplayNameChange(event.target.value)}
              placeholder="Jane Doe"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="resident-email">Email</Label>
              <Input
                id="resident-email"
                type="email"
                value={email}
                onChange={(event) => onEmailChange(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="resident-phone">Phone</Label>
              <Input
                id="resident-phone"
                value={phone}
                onChange={(event) => onPhoneChange(event.target.value)}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="resident-address">Home address (optional)</Label>
            <Input
              id="resident-address"
              value={addressLine1}
              onChange={(event) => onAddressLine1Change(event.target.value)}
              placeholder="123 Main St"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="resident-city">City</Label>
              <Input
                id="resident-city"
                value={city}
                onChange={(event) => onCityChange(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="resident-state">State</Label>
              <Input
                id="resident-state"
                value={state}
                onChange={(event) => onStateChange(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="resident-postal">Postal code</Label>
              <Input
                id="resident-postal"
                value={postalCode}
                onChange={(event) => onPostalCodeChange(event.target.value)}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={!canSubmit || saving}>
            {saving ? "Saving…" : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
